import { useRouter } from 'next/router'
import { useState, VFC } from 'react'
import { useNotification } from 'src/api/notification'
import { CloseGreenIcon, GreenDownArrowIcon, Logo } from 'src/assets/svgs'
import { ModalFrameFlexEnd } from 'src/components/hoc/asModal'
import { UserIcon } from 'src/components/Image/UserIcon'
import { SmallButton } from 'src/elements/Button'
import { Link } from 'src/elements/Link'
import { EMPTY_NOTIFICATION } from 'src/models/Notification'
import { LoginUser } from 'src/models/User'
import { isPaidMember } from 'src/models/User/role'
import { AuthService } from 'src/services/AuthService'
import {
  _menuorange,
  _message_tag_label,
  cautionRed,
  facebookBlue,
  signoutGray,
  trueblack,
  truewhite,
} from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import { absoluteFill } from 'src/styles/mixins'
import { getFirstDayOfNextMonth } from 'src/utils/dateUtils'
import {
  AUTH_FACEBOOK,
  CONTACT,
  CONTENTS,
  CONTRIBUTIONS,
  EDIT_NEEDS,
  EDIT_PERSONA,
  EDIT_PROFILE,
  EDIT_SCHEDULE_URL,
  EDIT_SOLUTIONS,
  EVENTS,
  EXHIBITIONS,
  FEEDBACKS,
  GUIDE,
  MESSAGES,
  MYPAGE,
  NEEDS,
  NOTICES,
  NOTICE_CONFIGURATION,
  PREMIUM_CONTRIBUTIONS,
  RECOMMEND,
  REFERRER_MESSAGE_GROUP,
  REQUEST_BOX,
  TOP,
} from 'src/utils/routes'
import styled, { css } from 'styled-components'

export const ModalMenu: VFC<{
  isOpen: boolean
  close: VoidFunction
  user: LoginUser
}> = ({
  isOpen,
  close,
  user: {
    profile: { name, iconSrc },
    plan: { free, paid, role, isAdminRole, isReferrer },
    settings: { facebookLogin },
    company: { name: companyName, isActiveScheduleUrl },
    member,
  },
}) => {
  const messageTotal = member?.messageTotal
  const {
    data: {
      messageSendableCount: { count } = EMPTY_NOTIFICATION.messageSendableCount,
    } = EMPTY_NOTIFICATION,
  } = useNotification()

  const router = useRouter()
  const [isSettingsOpen, setSettingsOpen] = useState(false)

  return (
    <>
      <StyledModalFrame isOpen={isOpen}>
        <ModalOverlay />
        <Layout>
          <ModalMenuHeader>
            <LogoDiv
              onClick={() => {
                router.push(MYPAGE)
              }}
            >
              <Logo height="22px" width="73px" />
            </LogoDiv>
            <CloseButton onClick={close}>
              <CloseGreenIcon />
            </CloseButton>
          </ModalMenuHeader>
          <MenuSectionDiv>
            <ProfileDiv>
              <StyledUserIcon src={iconSrc} size="48px" />
              <UserInfoDiv>
                <UserInfoDivContent>
                  <h3>{companyName}</h3>
                  <h2>{name}</h2>
                </UserInfoDivContent>
                {isPaidMember(paid, role) && <Role>有料会員</Role>}
              </UserInfoDiv>
            </ProfileDiv>
          </MenuSectionDiv>
          <MenuBody>
            <MenuLink href={RECOMMEND} onClick={close}>
              今日のAIピック
            </MenuLink>
            {(isAdminRole || paid) && (
              <MenuLink href={NEEDS} onClick={close}>
                決裁者を探す
              </MenuLink>
            )}
            <MenuLink href={PREMIUM_CONTRIBUTIONS} onClick={close}>
              案件を探す
            </MenuLink>
            <MenuLink href={EXHIBITIONS} onClick={close}>
              発注先を探す
            </MenuLink>
            <MenuLink href={CONTENTS} onClick={close}>
              経営コラム
            </MenuLink>
            <MenuLink href={CONTRIBUTIONS} onClick={close}>
              掲示板
            </MenuLink>
            <MenuLink href={EVENTS} onClick={close}>
              イベント
            </MenuLink>
            <MenuLink href={REQUEST_BOX} onClick={close}>
              つながり一覧
            </MenuLink>
            <MenuLink href={MESSAGES} onClick={close}>
              メッセージ
            </MenuLink>
            <MenuLink href={FEEDBACKS} onClick={close}>
              アポイント・評価
            </MenuLink>
            <MenuLink href={GUIDE} onClick={close}>
              ガイド
            </MenuLink>
          </MenuBody>
          <MenuBody>
            <SettingsToggle
              onClick={() => setSettingsOpen(!isSettingsOpen)}
              aria-expanded={isSettingsOpen}
            >
              <span>設定</span>
              <ToggleArrow isOpen={isSettingsOpen}>
                <GreenDownArrowIcon width="16px" height="10px" />
              </ToggleArrow>
            </SettingsToggle>
            {isSettingsOpen && (
              <>
                <MenuLink href={EDIT_PROFILE} onClick={close}>
                  プロフィールの編集
                </MenuLink>
                {/* Temporarily hidden - story list editing feature
                <MenuLink href={EDIT_STORYLIST}>ストーリー記事を編集</MenuLink>
                */}
                <MenuLink href={EDIT_NEEDS} onClick={close}>
                  ニーズの編集
                </MenuLink>
                <MenuLink href={EDIT_SOLUTIONS} onClick={close}>
                  ソリューションの編集
                </MenuLink>
                <MenuLink href={EDIT_PERSONA} onClick={close}>
                  ペルソナ登録
                </MenuLink>
                {isActiveScheduleUrl && (
                  <MenuLink href={EDIT_SCHEDULE_URL} onClick={close}>
                    日程調整URL登録
                  </MenuLink>
                )}
                {/* <MenuLink href={USAGE_HISTORIES}>利用ステータス</MenuLink> */}
                <MenuLink href={NOTICE_CONFIGURATION} onClick={close}>
                  通知設定
                </MenuLink>
                <MenuLink href={CONTACT} onClick={close}>
                  お問い合わせ
                </MenuLink>
                <MenuLink href={NOTICES} onClick={close}>
                  運営からのお知らせ
                </MenuLink>
                {isReferrer && (
                  <MenuLinkInhouse
                    href={REFERRER_MESSAGE_GROUP}
                    onClick={close}
                  >
                    【管理】メッセージグループ
                  </MenuLinkInhouse>
                )}
              </>
            )}
          </MenuBody>
          {!free && (
            <MenuBody>
              <p>送信可能回数</p>
              {messageTotal === 99999 ? (
                <AvailableCount>
                  <span>無制限</span>
                </AvailableCount>
              ) : (
                <>
                  <AvailableCount>
                    残り <span>{count}</span> 通
                  </AvailableCount>
                  <NextCount>{getFirstDayOfNextMonth()}に追加予定</NextCount>
                </>
              )}
            </MenuBody>
          )}
          <MenuBody>
            <FacebookButton onClick={() => router.push(AUTH_FACEBOOK)}>
              {facebookLogin ? '連携解除' : 'Facebook連携'}
            </FacebookButton>
            <LogoutButton
              onClick={() =>
                AuthService.signOut().then(() => window.location.assign(TOP))
              }
            >
              ログアウトはこちら
            </LogoutButton>
          </MenuBody>
        </Layout>
      </StyledModalFrame>
    </>
  )
}
const StyledModalFrame = styled(ModalFrameFlexEnd)<{ isOpen: boolean }>`
  transition: all 0.5s ease-out;
  clip-path: inset(0 100% 0 0);
  transform: translateX(100%);
  visibility: hidden;
  ${({ isOpen }) =>
    isOpen &&
    css`
      visibility: visible;
      clip-path: inset(0);
      transform: translateX(0);
    `}
`
const LogoDiv = styled.div`
  cursor: pointer;
`
const CloseButton = styled.button``
const Layout = styled.div`
  position: relative;
  min-width: 312px;
  background-color: ${truewhite};
  height: 100%;
  padding: 10px 20px 40px;
  overflow-y: auto;
`
const ModalMenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`
const ModalOverlay = styled.div`
  ${absoluteFill};
  background-color: ${trueblack};
`

const MenuSectionDiv = styled.div`
  border-bottom: 1px solid ${_message_tag_label};
  width: 100%;
  padding-bottom: 16px;
  :last-child {
    border: none;
    padding-bottom: 10px;
  }
`

const MenuBody = styled(MenuSectionDiv)<{ collapsed?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;

  margin-top: 10px;

  > a,
  > button {
    margin-top: 10px;
  }
`

const StyledUserIcon = styled(UserIcon)`
  float: left;
`
const UserInfoDiv = styled.div`
  margin-left: 35%;
  font-weight: ${fontWeightBold};

  h3 {
    font-size: 12px;
  }
  h2 {
    margin-top: 4px;
    font-size: 14px;
    margin-bottom: 8px;
  }
`
const UserInfoDivContent = styled.div`
  height: 45px;
`
const Role = styled.p`
  width: 45%;
  border-radius: 15px;
  background-color: ${_menuorange};
  color: ${truewhite};
  text-align: center;
  padding: 2px;
`
const ProfileDiv = styled.div`
  position: relative;
  padding-top: 12px;
`

const AvailableCount = styled.p`
  margin-top: 32px;
  span {
    font-size: 25px;
    color: ${_menuorange};
  }
`

const NextCount = styled.p`
  margin-top: 10px;
  font-size: 11px;
  font-weight: normal;
  color: #808080;
`

const MenuLink = styled(Link)`
  :hover,
  :focus {
    text-decoration: underline;
  }
`
const SettingsToggle = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 0;
  font-weight: ${fontWeightBold};
`
const ToggleArrow = styled.span<{ isOpen: boolean }>`
  display: flex;
  transition: transform 0.3s;
  transform: rotate(${({ isOpen }) => (isOpen ? '0deg' : '-90deg')});
`
const MenuLinkInhouse = styled(Link)`
  color: ${cautionRed};
  :hover,
  :focus {
    text-decoration: underline;
  }
`
const ButtonCss = css`
  height: 53px;
  width: 200px;
  color: white;
`
const FacebookButton = styled(SmallButton)`
  ${ButtonCss}
  background: ${facebookBlue};
`
const LogoutButton = styled(SmallButton)`
  ${ButtonCss}
  background: ${signoutGray};
`
